import React from 'react';
import PropTypes from 'prop-types';

import AceEditor from 'react-ace';
import 'brace';
import 'brace/ext/searchbox';
import 'brace/mode/html';
import 'brace/mode/json';
import 'brace/theme/terminal';
import 'brace/theme/textmate';
import classNames from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard } from '@fortawesome/pro-solid-svg-icons';

import ContentEditor from 'bundles/email/content_editor';
import IFrame from 'components/iframe';
import PreviewType, { previewTypes } from './preview/preview_type';

import './preview/styles/preview.scss';

class Preview extends React.Component {
  static propTypes = {
    contentEditorStylesPath: PropTypes.string,
    designer: PropTypes.bool,
    error: PropTypes.string,
    liquidContext: PropTypes.string,
    html: PropTypes.string.isRequired,
    htmlWithOverrides: PropTypes.string,
    onlyWeb: PropTypes.bool,
    overrides: PropTypes.object,
    contentHeader: PropTypes.object,
    readOnly: PropTypes.bool,
    theme: PropTypes.string,
    updating: PropTypes.bool,

    onAiInteraction: PropTypes.func,
    onOverridesChange: PropTypes.func,
    onOverridesReset: PropTypes.func,
    onPreviewTypeChange: PropTypes.func
  }

  static defaultProps = {
    overrides: {},
    theme: '',
    onOverridesChange: () => {},
    onOverridesReset: () => {},
    onPreviewTypeChange: () => {},
  }

  state = {
    copied: false,
    dirty: false,
    previewType: previewTypes.web
  }

  htmlPreview = React.createRef()

  componentDidUpdate(previousProps) {
    if (this.htmlPreview.current) {
      // Disable syntax validation
      this.htmlPreview.current.editor.getSession().setUseWorker(false);
    }

    if (this.props.htmlWithOverrides !== previousProps.htmlWithOverrides) {
      this.setState({ dirty: false });
    }
  }

  handleCopy = () => {
    this.setState({ copied: true });
  }

  handleOverridesChange = (...args) => {
    this.setState({ dirty: true });
    this.props.onOverridesChange(...args);
  }

  handleOverridesReset = (...args) => {
    this.setState({ dirty: true });
    this.props.onOverridesReset(...args);
  }

  handlePreviewTypeChange = (previewType) => {
    this.setState({ previewType }, () => {
      const dirty = (previewType === previewTypes.html || previewType === previewTypes.liquid) && this.state.dirty;
      this.props.onPreviewTypeChange(previewType, dirty);
    });
  }

  render() {
    const className = classNames('email-preview', this.state.previewType, this.props.theme, {
      error: this.props.error,
      updating: this.props.updating
    });

    return (
      <div className={className}>
        <div className="email-preview-wrapper">
          {this.renderPreview()}
        </div>
      </div>
    );
  }

  renderError() {
    if (!this.props.error) {
      return null;
    }

    return (
      <div className="alert alert-danger" name="error">
        {this.props.error}
      </div>
    );
  }

  renderPreview() {
    if (this.state.previewType === previewTypes.html) {
      return this.renderHtmlPreview();
    } else if (this.state.previewType === previewTypes.liquid) {
      return this.renderLiquidPreview();
    } else if (this.props.readOnly) {
      return this.renderContentPreview();
    } else {
      return this.renderContentEditor();
    }
  }

  renderContentEditor() {
    const header = <React.Fragment>
      {this.props.contentHeader}
      {this.renderError()}
    </React.Fragment>;

    return <React.Fragment>
      <ContentEditor
        content={this.props.html}
        overrides={this.props.overrides}
        header={header}
        renderPreviewType={this.renderPreviewType}
        stylesPath={this.props.contentEditorStylesPath}
        onAiInteraction={this.props.onAiInteraction}
        onOverridesChange={this.handleOverridesChange}
        onOverridesReset={this.handleOverridesReset}
      />
    </React.Fragment>;
  }

  renderContentPreview() {
    return <React.Fragment>
      <div className="email-preview-toolbar">
        {this.renderPreviewType()}
      </div>

      {this.props.contentHeader}
      {this.renderError()}

      <IFrame content={this.props.html} />
    </React.Fragment>;
  }

  renderHtmlPreview() {
    const html = this.props.htmlWithOverrides || this.props.html;

    return <React.Fragment>
      <div className="email-preview-toolbar">
        {this.renderPreviewType()}

        <div>
          {this.state.copied ? "Copied!" : null }

          <CopyToClipboard text={html} onCopy={this.handleCopy}>
            <button className="btn btn-sm btn-primary">
              <FontAwesomeIcon icon={faClipboard} size="lg" />
              Copy to Clipboard
            </button>
          </CopyToClipboard>
        </div>
      </div>

      {this.renderError()}

      <AceEditor
        className="html-preview"
        editorProps={{ $blockScrolling: Infinity }}
        mode="html"
        readOnly={true}
        ref={this.htmlPreview}
        showPrintMargin={false}
        theme={this.props.theme === 'dark' ? 'terminal' : 'textmate'}
        width="100%"
        height="100%"
        value={html}
      />
    </React.Fragment>;
  }

  renderLiquidPreview() {
    return <React.Fragment>
      <div className="email-preview-toolbar">
        {this.renderPreviewType()}
      </div>

      {this.renderError()}

      <AceEditor
        className="liquid-preview"
        editorProps={{ $blockScrolling: Infinity }}
        mode="json"
        readOnly={true}
        showPrintMargin={false}
        theme={this.props.theme === 'dark' ? 'terminal' : 'textmate'}
        width="100%"
        height="100%"
        value={this.props.liquidContext || ''}
      />
    </React.Fragment>;
  }

  renderPreviewType = () => {
    if (this.props.onlyWeb) {
      return null;
    }

    return (
      <PreviewType
        designer={this.props.designer}
        previewType={this.state.previewType}
        onChange={this.handlePreviewTypeChange}
      />
    );
  }
}

export default Preview;
